<template>
  <div>
    <Head title="International Suppliers" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-orange-400 hover:text-orange-600" :href="`/international/purchaseOrders/${purchaseOrder.id}`">Shipping Information</Link>
      <span class="text-orange-400 font-medium">/</span>Create
    </h1>
    <div class="max-w-full bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="store">
        <div class="flex flex-wrap -mb-16 -mr-6 p-8">
          <text-input v-model="form.PO_number" type="text" class="pb-8 pr-6 w-full lg:w-4/4" label="Purchase Order" />
        </div>
        <div class="pb-2 pr-6 w-full lg:w-full mb-8">
          <div
            v-for="(shipping, index) in form.shippings"
            :key="index"
            class="flex flex-wrap -mb-16 -mr-6 p-8"
          >
            <div class="pb-2 pr-6 w-full lg:w-2/12">
              <select-input
                v-model="shipping.shipment_line_id"
                class="w-full lg:w-ful"
              >
                <option :value="null">
                  Select Shipment Line
                </option>
                <option
                  v-for="shipmentLine in shipmentLines"
                  :key="index"
                  :value="shipmentLine.id"
                >
                  {{ shipmentLine.name }}
                </option>
              </select-input>
              <span
                v-if="isValidationErrors(`shippings.${index}.shipment_line_id`)"
                class="mt-3"
              >
                <span class="form-error">{{ showValidationErrors(`shippings.${index}.shipment_line_id`) }}</span>
              </span>
            </div>
            <div class="pb-2 pr-6 w-full lg:w-2/12">
              <select-input
                v-model="shipping.port_id"
                class="w-full lg:w-ful"
              >
                <option :value="null">
                  Select Ports
                </option>
                <option
                  v-for="port in ports"
                  :key="index"
                  :value="port.id"
                >
                  {{ port.name }}
                </option>
              </select-input>
              <span
                v-if="isValidationErrors(`shippings.${index}.port_id`)"
                class="mt-3"
              >
                <span class="form-error">{{ showValidationErrors(`shippings.${index}.port_id`) }}</span>
              </span>
            </div>
            <div class="pb-2 pr-6 w-full lg:w-2/12">
              <text-input v-model="shipping.container_number" type="text" class="w-full lg:w-ful" placeholder="Container Number" />
              <span
                v-if="isValidationErrors(`shippings.${index}.container_number`)"
                class="mt-3"
              >
                <span class="form-error">{{ showValidationErrors(`shippings.${index}.container_number`) }}</span>
              </span>
            </div>
            <div class="pb-2 pr-6 w-full lg:w-3/12">
              <select-input
                v-model="shipping.sku_id"
                class="w-full lg:w-ful"
              >
                <option :value="null">
                  Select SKU
                </option>
                <option
                  v-for="orderedProduct in orderedProducts"
                  :key="index"
                  :value="orderedProduct.sku_id"
                >
                  {{ orderedProduct.part_number }}: {{ orderedProduct.description }}
                </option>
              </select-input>
              <span
                v-if="isValidationErrors(`shippings.${index}.sku_id`)"
                class="mt-3"
              >
                <span class="form-error">{{ showValidationErrors(`shippings.${index}.sku_id`) }}</span>
              </span>
            </div>
            <div class="pb-2 pr-6 w-full lg:w-2/12">
              <text-input
                :key="index"
                class="w-full lg:w-full"
                v-model="shipping.quantity"
                placeholder="Quantity"
              />
              <span
                v-if="isValidationErrors(`shippings.${index}.quantity`)"
                class="mt-3"
              >
                <span class="form-error">{{ showValidationErrors(`shippings.${index}.quantity`) }}</span>
              </span>
            </div>
            <div class="pb-2 pr-6 w-full lg:w-1/12">
              <div class="w-full lg:w-1/2">
                <svg
                  v-show="form.shippings.length > 1"
                  @click="removeShipping(index, form.shippings)"
                  xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" style="color: red;">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="w-full lg:w-1/2">
                <svg
                  v-show="index === countShippings"
                  @click="addShipping"
                  xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div
          class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100"
        >
          <loading-button :loading="form.processing" class="btn-orange" type="submit">
            Add Shipping Information
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
  },
  layout: Layout,
  remember: 'form',
  props: {
    supplier: Object,
    orderedProducts: Array,
    purchaseOrder: Object,
    shipmentLines: Array,
    ports: Object,
  },
  data() {
    return {
      form: this.$inertia.form({
        PO_number: this.purchaseOrder.PO_number,
        shippings: [{
          sku_id: null,
          port_id: null,
          quantity: null,
          container_number: null,
          shipment_line_id: null,
        }],
      }),
    }
  },
   computed: {
    countShippings() {
      if (this.form.shippings.length === 0) {
        return 0;
      }
      return this.form.shippings.length - 1;
    },
  },
  methods: {
    addShipping(e) {
      e.preventDefault();
      this.form.shippings.push({
        sku_id: null,
        port_id: null,
        quantity: null,
        container_number: null,
        shipment_line_id: null,
      });
    },
    isValidationErrors(value) {
      if (this.form.errors) {
        const error = this.form.errors[value] || '';
        return !!error;
      }
      return false;
    },
    showValidationErrors(value) {
      if (this.form.errors) {
        const error = this.form.errors[value] || '';
        return error || '';
      }
      return '';
    },
    removeShipping(index, shippings) {
      shippings.splice(index, 1)
    },
    store() {
      this.form.post(`/international/purchaseOrders/${this.purchaseOrder.id}/book-store`, {
        onSuccess: () => {
          this.form.shippings.reset(
            'sku_id',
            'port_id',
            'shipment_line_id',
            'quality',
            'container_number',
          )
        }
      })
    },
  },
}
</script>
